// ÁTTEKINTÉS — „Figyelmet kér": a csendben elromló dolgok listája (ops-kártya).
//
// MIÉRT (best practice): a „my work" a FELHASZNÁLÓ teendőit adja, ez viszont a RENDSZER azon
// állapotait, amiket senki nem kap meg taszkként, mégis fájnak, ha kimaradnak:
//   • bankkártya, ami 60 napon belül lejár (`payment_cards.view`) — lejárt kártyán a szolgáltatás-fizetés bukik,
//   • a HAVI usage-lekérés, ami erre a hónapra még nem futott le (`usage_data.view`) — enélkül a számlázás
//     alul-számláz (a legacyben ez visszatérő hiba-forrás volt).
//
// A KÁRTYA CSAK AKKOR RENDEREL, ha van legalább egy tétel — üres állapotban NEM foglal helyet (nem „minden
// rendben" felirattal tölti a lapot; a nyugtázás a „Rám vár" kártya dolga).
import {
  ClockIcon,
  CreditCardIcon,
  DocumentMagnifyingGlassIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/20/solid';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { CardRow, CardRows } from '@/components/shared/card-rows';
import { PanelCard } from '@/components/shared/panel-card';
import type { AttentionData } from '@/features/dashboard/use-dashboard-data';

export interface AttentionCardProps {
  data: AttentionData | null;
  loading: boolean;
}

export function AttentionCard({ data, loading }: AttentionCardProps): JSX.Element | null {
  const { t } = useTranslation();
  const expiring = data?.expiringCards ?? 0;
  const usageMissing = data?.usageMissing ?? false;
  const pendingStartingSoon = data?.pendingStartingSoon ?? 0;
  const generationSkipsOpen = data?.generationSkipsOpen ?? 0;
  const hasAny = expiring > 0 || usageMissing || pendingStartingSoon > 0 || generationSkipsOpen > 0;

  // Betöltés közben sem foglalunk helyet: a kártya vagy VAN (mert van tétel), vagy nincs — a lap nem ugrik.
  if (loading || data === null || !hasAny) return null;

  return (
    <PanelCard capped title={t('dashboard.attention.title')}>
      <CardRows>
        {expiring > 0 && (
          <CardRow>
            <Link
              to="/payment-cards"
              className="hover:bg-accent/60 focus-visible:ring-ring flex items-center gap-3 px-4 py-2.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset"
            >
              <CreditCardIcon className="text-warning size-4 shrink-0" aria-hidden />
              <span className="text-md text-foreground min-w-0 flex-1">
                {t('dashboard.attention.expiringCards', { count: expiring })}
              </span>
            </Link>
          </CardRow>
        )}
        {usageMissing && (
          <CardRow>
            <Link
              to="/usage-data"
              className="hover:bg-accent/60 focus-visible:ring-ring flex items-center gap-3 px-4 py-2.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset"
            >
              <ExclamationTriangleIcon className="text-warning size-4 shrink-0" aria-hidden />
              <span className="text-md text-foreground min-w-0 flex-1">
                {t('dashboard.attention.usageMissing', { month: data.usageMonth })}
              </span>
            </Link>
          </CardRow>
        )}
        {pendingStartingSoon > 0 && (
          <CardRow>
            <Link
              to="/service-assignments"
              search={{ status: 'pending' }}
              className="hover:bg-accent/60 focus-visible:ring-ring flex items-center gap-3 px-4 py-2.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset"
            >
              <ClockIcon className="text-warning size-4 shrink-0" aria-hidden />
              <span className="text-md text-foreground min-w-0 flex-1">
                {t('dashboard.attention.pendingStartingSoon', { count: pendingStartingSoon })}
              </span>
            </Link>
          </CardRow>
        )}
        {generationSkipsOpen > 0 && (
          <CardRow>
            <Link
              to="/invoices"
              search={{ tab: 'skips' }}
              className="hover:bg-accent/60 focus-visible:ring-ring flex items-center gap-3 px-4 py-2.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset"
            >
              <DocumentMagnifyingGlassIcon className="text-warning size-4 shrink-0" aria-hidden />
              <span className="text-md text-foreground min-w-0 flex-1">
                {t('dashboard.attention.generationSkipsOpen', { count: generationSkipsOpen })}
              </span>
            </Link>
          </CardRow>
        )}
      </CardRows>
    </PanelCard>
  );
}
